import React, { useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import {
  StyleSheet,
  Text,
  View,
  Pressable,
  ScrollView,
  Platform,
} from 'react-native';
import { GTFonts, useTheme } from '../../theme';
import {
  formatWithCommas,
  getCurrencyDisplayFormat,
  s,
  fs,
  iconSize,
  v,
} from '../../utils/libs';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import moment from 'moment';
import { getOwnerSummary } from '../../api/backofficeSummary';
import {
  clearSummary,
  setSummaryData,
  setSummaryError,
  setSummaryLoading,
  SummarySliceObject,
} from '../../redux/backoffice/summarySlice';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../../navigation/AppStack';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import DateRangeDropdown from '../../components/Reusables/headers/DateRangeDropdown';
import { setSummaryRange } from '../../redux/backoffice/summarySlice';
import HeaderLeft from '../../components/Reusables/headers/HeaderLeft';
import BackOfficeHeaderLocationPicker from '../../components/Reusables/headers/BackOfficeHeaderLocationPicker';
import { useSecureNavigate } from '../../navigation/security/useSecureNavigate';

const BackOfficeSummaryScreen: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const dispatch = useAppDispatch();
  const nav = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { secureNavigate } = useSecureNavigate(nav as any);

  const employee = useAppSelector(
    s => s.auth.inApp.account.employeeInfo as any,
  );
  const ownerId = String(employee?.bp_owner_id || '');
  const locationVersion = useAppSelector(s => s.location.locationVersion);
  const selectedRange = useAppSelector(
    s =>
      (s as any).backofficeSummary
        .selectedRange as SummarySliceObject['selectedRange'],
  );
  const data = useAppSelector(s => (s as any).backofficeSummary.data);
  const loading = useAppSelector(s => (s as any).backofficeSummary.loading);
  const error = useAppSelector(s => (s as any).backofficeSummary.error);
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => getCurrencyDisplayFormat(currency),
    [currency],
  );

  const getRange = useCallback((selection: string) => {
    switch (selection) {
      case 'Today':
        return {
          start: moment().startOf('day').toISOString(),
          end: moment().endOf('day').toISOString(),
        };
      case 'Yesterday':
        return {
          start: moment().subtract(1, 'day').startOf('day').toISOString(),
          end: moment().subtract(1, 'day').endOf('day').toISOString(),
        };
      case 'Weekly':
        return {
          start: moment().startOf('week').toISOString(),
          end: moment().endOf('week').toISOString(),
        };
      case 'Monthly':
        return {
          start: moment().startOf('month').toISOString(),
          end: moment().endOf('month').toISOString(),
        };
      case 'Yearly':
        return {
          start: moment().startOf('year').toISOString(),
          end: moment().endOf('year').toISOString(),
        };
      default:
        return {
          start: moment().startOf('day').toISOString(),
          end: moment().endOf('day').toISOString(),
        };
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      if (!owner?.accessToken || !ownerId) return;
      dispatch(setSummaryLoading(true));
      dispatch(setSummaryError(null));
      // Capture the range at invocation time to guard against race conditions
      const requestRange = selectedRange;
      const { start, end } = getRange(requestRange);
      const res = await getOwnerSummary(
        axiosBackoffice as any,
        ownerId,
        start,
        end,
        { accessToken: owner?.accessToken },
      );
      console.log('backoffice summary', res?.data);
      if (res?.status === 200) {
        const payload = (res?.data?.data ?? res?.data) || {};
        dispatch(
          setSummaryData({
            range: requestRange as any,
            data: {
              duePaymentsAmount: Number(payload?.duePaymentsAmount ?? 0),
              lowStocksCount: Number(payload?.lowStocksCount ?? 0),
              expensesAmount: Number(payload?.expensesAmount ?? 0),
              stockSellValueAmount: Number(payload?.stockSellValueAmount ?? 0),
              profitAmount: Number(payload?.profitAmount ?? 0),
              completedOrdersCount: Number(payload?.completedOrdersCount ?? 0),
              pendingOrdersCount: Number(payload?.pendingOrdersCount ?? 0),
              partialOrdersCount: Number(payload?.partialOrdersCount ?? 0),
              staffCount: Number(payload?.staffCount ?? 0),
            },
          }),
        );
      } else {
        dispatch(
          setSummaryError(
            String(res?.data?.message || 'Failed to load summary'),
          ),
        );
      }
    } catch (e: any) {
      dispatch(setSummaryError(e?.message || 'Failed to load summary'));
    } finally {
      dispatch(setSummaryLoading(false));
    }
  }, [
    axiosBackoffice,
    dispatch,
    getRange,
    owner?.accessToken,
    ownerId,
    selectedRange,
  ]);

  useLayoutEffect(() => {
    nav.setOptions({
      headerTintColor: theme.colors.textInverse,
      headerTitleAlign: Platform.OS === 'web' ? 'center' : 'left',
      headerTitleStyle: {
        fontSize: fs(18),
        fontFamily: GTFonts.semibold,
      },
      headerTitle: () => (
        <BackOfficeHeaderLocationPicker
          containerStyle={
            Platform.OS === 'web' ? undefined : { marginLeft: s(10) }
          }
        />
      ),
      headerLeft: props =>
        Platform.OS === 'web' ? (
          <HeaderLeft {...props} onPress={() => nav.goBack()} />
        ) : null,
      headerRight: () => (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginRight: s(16),
          }}>
          <DateRangeDropdown
            variant="compact"
            chipBgColor={'rgba(255,255,255,0.15)'}
            chipTextColor={theme.colors.textInverse}
            chipBorderColor={'rgba(255,255,255,0.35)'}
            selectedOverride={selectedRange}
            onChange={v => dispatch(setSummaryRange(v as any))}
          />
        </View>
      ),
    });
  }, [nav, dispatch, selectedRange]);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary, locationVersion]);

  const MetricCard: React.FC<{
    label: string;
    value?: string;
    onPress: () => void;
    valueColor?: string;
    valueNode?: React.ReactNode;
  }> = ({ label, value, onPress, valueColor, valueNode }) => (
    <Pressable
      style={({ pressed, hovered }: any) => [
        styles.card,
        Platform.OS === 'web' && styles.cardWeb,
        Platform.OS === 'web' && hovered && styles.cardHovered,
        pressed && { opacity: 0.85 },
      ]}
      onPress={onPress}>
      {valueNode ? (
        <View style={styles.valueNodeContainer}>{valueNode}</View>
      ) : (
        <Text
          style={[styles.valueTop, valueColor ? { color: valueColor } : null]}>
          {value}
        </Text>
      )}
      <Text style={styles.labelBottom}>{label}</Text>
    </Pressable>
  );

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}>
        <View style={styles.pageHeader}>
          <Text style={styles.pageTitle}>Business Summary</Text>
        </View>
        {error ? (
          <View style={styles.errorWrap}>
            <>
              <MaterialCommunityIcons
                name="alert"
                size={iconSize(28)}
                color={theme.colors.error}
              />
              <Text style={styles.errorText}>{error}</Text>
            </>
          </View>
        ) : (
          <View style={{ height: v(20) }} />
        )}

        <View style={styles.grid}>
          <MetricCard
            label="Stock value (Sell)"
            value={`${currencyText} ${formatWithCommas(Number(data?.stockSellValueAmount || 0), 0)}`}
            valueColor={theme.colors.primary}
            onPress={() =>
              void secureNavigate('BackOffice', { screen: 'Sales' })
            }
          />

          <MetricCard
            label="Profit"
            value={`${currencyText} ${formatWithCommas(Number(data?.profitAmount || 0), 0)}`}
            valueColor={theme.colors.success}
            onPress={() =>
              void secureNavigate('BackOffice', { screen: 'Sales' })
            }
          />
          <MetricCard
            label="Low stocks"
            value={String(Number(data?.lowStocksCount || 0))}
            valueColor={theme.colors.error}
            onPress={() =>
              void secureNavigate('BackOffice', {
                screen: 'Inventory',
                params: { preset: 'low' },
              })
            }
          />
          <MetricCard
            label="Staff"
            value={String(Number(data?.staffCount || 0))}
            valueColor={theme.colors.primary}
            onPress={() => void secureNavigate('BackOfficeEmployees')}
          />
          <MetricCard
            label="Due payments"
            value={`${currencyText} ${formatWithCommas(Number(data?.duePaymentsAmount || 0), 0)}`}
            valueColor={theme.colors.primary}
            onPress={() =>
              void secureNavigate('BackOffice', {
                screen: 'Orders',
                params: {
                  preset: 'pending',
                  hideCompleted: true,
                  allUnpaid: true,
                },
              })
            }
          />

          <MetricCard
            label="Expenses"
            value={`${currencyText} ${formatWithCommas(Number(data?.expensesAmount || 0), 0)}`}
            valueColor={theme.colors.primary}
            onPress={() =>
              void secureNavigate('BackOffice', { screen: 'Expenses' })
            }
          />
          <MetricCard
            label="Orders"
            onPress={() =>
              void secureNavigate('BackOffice', { screen: 'Orders' })
            }
            valueNode={
              <>
                <Text style={styles.ordersValueLine}>
                  Completed{'\u00A0'}
                  <Text
                    style={[
                      styles.ordersValueCount,
                      { color: theme.colors.success },
                    ]}>
                    {String(Number(data?.completedOrdersCount || 0))}
                  </Text>
                </Text>
                <Text style={styles.ordersValueLine}>
                  Pending{'\u00A0'}
                  <Text
                    style={[
                      styles.ordersValueCount,
                      { color: theme.colors.error },
                    ]}>
                    {String(Number(data?.pendingOrdersCount || 0))}
                  </Text>
                </Text>
                <Text style={styles.ordersValueLine}>
                  Partial{'\u00A0'}
                  <Text
                    style={[
                      styles.ordersValueCount,
                      { color: theme.colors.warning },
                    ]}>
                    {String(Number(data?.partialOrdersCount || 0))}
                  </Text>
                </Text>
              </>
            }
          />
        </View>
      </ScrollView>
    </View>
  );
};

export default BackOfficeSummaryScreen;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    content: {
      padding: s(8),
      rowGap: v(12),
    },
    pageHeader: {
      paddingHorizontal: s(8),
      paddingTop: v(12),
    },
    pageTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(18),
      lineHeight: fs(24),
      textAlign: 'center',
      color: theme.colors.text,
    },
    grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      rowGap: v(10),
      margin: v(8),
    },
    card: {
      width: '48%',
      minHeight: v(Platform.OS === 'web' ? 150 : 120),
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      paddingVertical: s(14),
      paddingHorizontal: s(10),
      ...theme.shadows.md,
    },
    valueTop: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(22),
      lineHeight: fs(26),
      textAlign: 'center',
      marginTop: v(10),
      marginBottom: v(20),
      color: theme.colors.primary,
    },
    valueNodeContainer: {
      marginTop: v(10),
      marginBottom: v(12),
      alignItems: 'center',
      justifyContent: 'center',
      gap: v(2) as unknown as number,
    },
    ordersValueLine: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      textAlign: 'center',
      color: theme.colors.text,
    },
    ordersValueCount: {
      fontFamily: GTFonts.semibold,
      color: theme.colors.primary,
    },
    labelBottom: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(18),
      textAlign: 'center',
      color: theme.colors.text,
    },
    errorWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6) as unknown as number,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      padding: s(8),
      marginHorizontal: s(8),
    },
    errorText: {
      color: theme.colors.error,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
    },
    cardWeb: {
      ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
    },
    cardHovered: {
      backgroundColor: 'rgba(21,96,189,0.12)',
    },
  });
